import { z } from "zod";
import {
    InvoiceLineItemKind,
    InvoiceSource,
    InvoiceStatus,
} from "@common/types/invoice";
import { NativeInvoiceTermType } from "@common/helpers/invoice-terms.helper";
import { matchedScheduleSchema } from "../accounting/scheduleMatchedDisplay.schema";
import {
    documentLineScheduleSchema,
    documentScheduleBaseSchema,
} from "../accounting/scheduleDocumentDisplay.schema";
import { counterpartySchema } from "../../counterparty.schema";
import { classAssignmentSchema } from "../../class.schema";
import { financialAccountWithIntegrationAccountsResponseSchema } from "../../financialAccount.schema";
import { accountOverrideEntryResponseSchema } from "../accounting/account-override.schema";
import {
    financialDocumentLinksResponseSchema,
    toFinancialDocumentIds,
} from "../../financialDocument/financial-document-links-response.schema";
import { invoiceSentInfoSchema } from "./invoice-sent-info.schema";

export const invoiceLineItemScheduleSchema = documentLineScheduleSchema;

export type InvoiceLineItemSchedule = z.infer<
    typeof invoiceLineItemScheduleSchema
>;

// Scope tells the invoice-level Schedule column which of its states to render:
// a whole-invoice application, or one or more line-scoped ones (which block
// whole-invoice apply and surface as "Partially scheduled").
export const invoiceScheduleScopeSchema = z.enum(["invoice", "lines"]);

export const invoiceScheduleSchema = documentScheduleBaseSchema.extend({
    scope: invoiceScheduleScopeSchema,
});

export type InvoiceSchedule = z.infer<typeof invoiceScheduleSchema>;

export const invoiceLineItemResponseSchema = z.object({
    id: z.string(),
    amount: z.number(),
    matchedAmount: z.number(),
    schedule: invoiceLineItemScheduleSchema.optional(),
    // Only set when neither the line nor its invoice has a schedule.
    matchedSchedules: z.array(matchedScheduleSchema).optional(),
    unitAmount: z.number().nullable().optional(),
    currency: z.string(),
    description: z.string().nullable(),
    quantityDecimal: z.number().nullable(),
    source: z.nativeEnum(InvoiceSource),
    sourceId: z.string(),
    accountId: z.string().nullable().optional(),
    accountsReceivableId: z.string().nullable().optional(),
    classAssignments: z.array(classAssignmentSchema).optional(),
    invoiceId: z.string(),
    kind: z.nativeEnum(InvoiceLineItemKind),
    rowId: z.string().nullable(),
    accountOverrides: z.array(accountOverrideEntryResponseSchema).optional(),
    accountsReceivableOverrides: z
        .array(accountOverrideEntryResponseSchema)
        .optional(),
    nativeDetail: z
        .object({ productOrService: z.string().nullable() })
        .nullable()
        .optional(),
});

const invoiceSharedResponseSchema = z.object({
    schedule: invoiceScheduleSchema.optional(),
    // Only set when the invoice has no schedule of its own.
    matchedSchedules: z.array(matchedScheduleSchema).optional(),
    id: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    due: z.coerce.date().nullable(),
    paidAt: z.coerce.date().nullable(),
    uncollectableAt: z.string().nullable(),
    voidedAt: z.string().nullable(),
    invoiceDate: z.string().nullable(),
    dateOnly: z.string().nullable(),
    paidDateOnly: z.string().nullable(),
    dueDateOnly: z.string().nullable(),
    total: z.number(),
    subtotal: z.number(),
    tax: z.number(),
    discount: z.number(),
    currency: z.string(),
    counterparty: counterpartySchema.nullable().optional(),
    description: z.string().nullable(),
    memo: z.string().nullable(),
    status: z.nativeEnum(InvoiceStatus),
    externalStatus: z.nativeEnum(InvoiceStatus).nullable(),
    source: z.nativeEnum(InvoiceSource),
    sourceId: z.string().nullable(),
    isSubscription: z.boolean(),
    lines: z.array(invoiceLineItemResponseSchema).optional(),
    entityId: z.number(),
    financialAccountId: z.number().nullable(),
    financialAccount: financialAccountWithIntegrationAccountsResponseSchema
        .nullable()
        .optional(),
    invoiceNumber: z.string().nullable(),
    revisedFromInvoiceId: z.string().nullable().optional(),
    sentInfo: invoiceSentInfoSchema.optional(),
});

/**
 * Wire-format Invoice DTO after `invoiceResponseSchema` transform.
 * Client list fetches parse this so date coercion runs without
 * requiring entity-only `financialDocumentLinks`.
 */
export const invoiceDtoSchema = invoiceSharedResponseSchema.extend({
    financialDocumentIds: z.array(z.number()),
    collectPayment: z.boolean(),
    paymentToken: z.string().uuid().nullable(),
    isTaxPerLineItem: z.boolean().default(false),
    isDiscountPerLineItem: z.boolean().default(false),
    isDepositPerLineItem: z.boolean().default(false),
    termType: z.nativeEnum(NativeInvoiceTermType).nullable(),
});

/**
 * Owns the InvoiceEntity → Invoice DTO boundary: unknown keys (e.g.
 * `sourceData`, line back-references) are stripped by Zod, and
 * `financialDocumentLinks` are reshaped into `financialDocumentIds`.
 * Controllers return entity rows directly; `validateResponses` produces
 * the DTO.
 */
export const invoiceResponseSchema = invoiceSharedResponseSchema
    .extend({
        financialDocumentLinks: financialDocumentLinksResponseSchema,
        nativeDetail: z
            .object({
                collectPayment: z.boolean(),
                paymentToken: z.string().uuid().nullable(),
                isTaxPerLineItem: z.boolean().default(false),
                isDiscountPerLineItem: z.boolean().default(false),
                isDepositPerLineItem: z.boolean().default(false),
                termType: z.nativeEnum(NativeInvoiceTermType).nullable(),
            })
            .nullable()
            .optional(),
    })
    .transform(
        ({
            financialDocumentLinks,
            nativeDetail,
            ...invoice
        }): z.infer<typeof invoiceDtoSchema> => ({
            ...invoice,
            financialDocumentIds: toFinancialDocumentIds(
                financialDocumentLinks,
            ),
            collectPayment: nativeDetail?.collectPayment ?? false,
            paymentToken: nativeDetail?.paymentToken ?? null,
            isTaxPerLineItem: nativeDetail?.isTaxPerLineItem ?? false,
            isDiscountPerLineItem: nativeDetail?.isDiscountPerLineItem ?? false,
            isDepositPerLineItem: nativeDetail?.isDepositPerLineItem ?? false,
            termType: nativeDetail?.termType ?? null,
        }),
    );

export const invoicesPageResponseSchema = z.object({
    total: z.number(),
    pageCount: z.number(),
    data: z.array(invoiceResponseSchema),
});

export const invoicesPageDtoSchema = z.object({
    total: z.number(),
    pageCount: z.number(),
    data: z.array(invoiceDtoSchema),
});
